// Lib
import React, { ReactElement } from 'react';
import classNames from 'classnames';
import { debounce } from 'lodash';

// Singletons
import zoomComponentSingleton from '../../../../canvas/zoom/zoomComponentSingleton';

// Components
import PopupPanelAsideSection from '../../../../components/popupPanel/types/asideSection/PopupPanelAsideSection';
import StyledButton from '../../../../components/buttons/StyledButton';
import Spinner from '../../../../components/loaders/Spinner';
import Slider from '../../../../components/slider/Slider';

// Utils
import platformSingleton from '../../../../platform/platformSingleton';
import { isPlatformApple } from '../../../../platform/utils/platformDetailsUtils';
import measurementsRegistry from '../../../../components/measurementsStore/measurementsRegistry';
import { hasChanged } from '../../../../utils/react/propsComparisons';
import { getMinScale } from '../../../../canvas/zoom/zoomScaleUtils';
import { convertKeyToEnvironment } from '../../../shortcuts/modal/ShortcutKey';
import { getSliderRailPositionFromValue } from '../../../../components/slider/sliderUtils';
import rafThrottle from '../../../../../common/utils/lib/rafThrottle';
import {
    decrementZoomScalePercentage,
    incrementZoomScalePercentage,
} from '../../../../canvas/zoom/utils/zoomStepUtils';
import { getTimestamp } from '../../../../../common/utils/timeUtil';
import { getTargetZoomToFitScale, getZoomToFitScale } from '../../../../canvas/zoom/utils/zoomToFitUtils';

// Constants
import { ZOOM_PROPS } from '../../../../canvas/zoom/zoomConstants';
import { KEY_CODES } from '../../../../utils/keyboard/keyConstants';

// Types
import { Rect, RectSize } from '../../../../../common/maths/geometry/rect/rectTypes';

// Styles
import './ZoomPopupPanelSection.scss';

const SHOW_SCALE_CLASS = 'show-scale';
const SHOW_RESET_CLASS = 'show-reset';

const hasIsCurrentBoardFetchedChanged = hasChanged('isCurrentBoardFetched');

interface Props {
    isCurrentBoardFetched: boolean;
    zoomScale: number;
    canvasSize: RectSize;
    currentUserEducation: number;
    dispatchSetCanvasZoomTargetScale: (targetScale: number) => void;
    dispatchGetCanvasZoomContentRectThunk: (canvasViewportClientRect: Rect) => Rect;
    dispatchGetZoomToFitContentRectThunk: (canvasViewportNode: HTMLElement) => Rect;
}

export const zoomSliderOptions = {
    min: ZOOM_PROPS.MIN_SCALE * 100,
    max: ZOOM_PROPS.MAX_SCALE * 100,
    scaleToFit: ZOOM_PROPS.MIN_SCALE * 100,
    checkpoint: 100,
    resetValue: 100,
    unit: '%',
};

class ZoomPopupPanelSection extends React.Component<Props> {
    private readonly zoomToFitButtonRef = React.createRef<HTMLButtonElement>();
    private readonly sliderHandleRef = React.createRef<HTMLButtonElement>();
    private initialisationTimeoutId?: NodeJS.Timeout;

    constructor(props: Props) {
        super(props);

        zoomComponentSingleton.setZoomPopupPanelInstance(this);

        this.initialiseZoomSliderOptions();

        // Use this to force a re-render when the min scale is manually changed
        this.state = {
            forcedRenderTime: 0,
        };
    }

    componentDidUpdate(prevProps: Readonly<Props>): void {
        if (hasIsCurrentBoardFetchedChanged(prevProps, this.props)) {
            // Give the content a chance to render
            this.initialisationTimeoutId = setTimeout(() => {
                this.initialiseZoomSliderOptions();
            }, 200);
        }
    }

    componentWillUnmount(): void {
        zoomComponentSingleton.removeZoomPopupPanelInstance();

        if (this.initialisationTimeoutId) clearTimeout(this.initialisationTimeoutId);
    }

    initialiseZoomSliderOptions = () => {
        const canvasViewportNode = measurementsRegistry.getCanvasViewportDomElement();

        if (!canvasViewportNode) return;

        const canvasViewportClientRect = canvasViewportNode.getBoundingClientRect();

        const contentRect = this.props.dispatchGetCanvasZoomContentRectThunk(canvasViewportClientRect);

        const zoomToFitContentRect = this.props.dispatchGetZoomToFitContentRectThunk(canvasViewportNode);
        const zoomToFitScale = getZoomToFitScale(zoomToFitContentRect, canvasViewportClientRect);

        zoomSliderOptions.min = getMinScale(canvasViewportClientRect, contentRect) * 100;
        zoomSliderOptions.scaleToFit = zoomToFitScale;
    };

    /**
     * Fires a fake "z" keypress event so that the key handler within the ZoomToFitHandler will execute.
     */
    onScaleToFitClick = (): void => {
        const { zoomScale } = this.props;

        const keyPress = new KeyboardEvent('keypress', {
            key: 'z',
            code: 'KeyZ',
            charCode: KEY_CODES.Z,
            keyCode: KEY_CODES.Z,
            which: KEY_CODES.Z,
        });

        document.dispatchEvent(keyPress);

        if (!this.zoomToFitButtonRef.current) return;

        const scaleToFitButtonTargetScale = getTargetZoomToFitScale(zoomScale, zoomSliderOptions.scaleToFit);
        const showResetText = scaleToFitButtonTargetScale !== 1;

        const classToAdd = showResetText ? SHOW_RESET_CLASS : SHOW_SCALE_CLASS;
        const classToRemove = !showResetText ? SHOW_RESET_CLASS : SHOW_SCALE_CLASS;

        this.zoomToFitButtonRef.current.classList.add(classToAdd);
        this.zoomToFitButtonRef.current.classList.remove(classToRemove);
    };

    handleZoomChange = (value: number): void => {
        this.props.dispatchSetCanvasZoomTargetScale(value / 100);
    };

    handleZoomWheel = (event: React.WheelEvent<HTMLDivElement>): void => {
        const canvasViewportElement = measurementsRegistry.getCanvasViewportDomElement();

        // Replicate a cmd + wheel event on the canvas viewport element to trigger zooming
        const newEvent = new WheelEvent('wheel', {
            deltaMode: event.deltaMode,

            // When wheeling horizontally on the slider, translate that to a delta Y wheel event on the canvas viewport
            deltaX: 0,
            deltaY: Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY,
            deltaZ: 0,

            metaKey: isPlatformApple(platformSingleton),
            ctrlKey: !isPlatformApple(platformSingleton),
        });

        canvasViewportElement.dispatchEvent(newEvent);
    };

    /**
     * When the minimum scale changes (e.g. because the viewport size changes) the slider must be
     * updated to match the new min scale. We must also force-render the component to ensure that the
     * slider click handlers correctly calculate the positions based on the new min value.
     */
    manuallySetMinScale = (minScale: number): void => {
        zoomSliderOptions.min = minScale * 100;
        this.debouncedForceRender();
    };

    /**
     * This static function is called by ZoomCanvas to force update the value of the zoom slider. It is required
     * to make sure the zoom slider is updated immediately in a performant way (without having to go through redux
     * state management)
     */
    manuallySetZoomValue = rafThrottle((zoomScale: number, targetZoomScale: number): void => {
        if (!this.sliderHandleRef.current) return;

        // Set the position of the slider handle to be based on the zoom scale set by ZoomCanvas
        const newValue = zoomScale * 100;
        this.sliderHandleRef.current.style.left = `${
            getSliderRailPositionFromValue(newValue, zoomSliderOptions) * 100
        }%`;

        // Set the slider handle text to be the zoom scale set by ZoomCanvas. If the target is 1 and zoom scale is very
        // close to 1, show the 100% in the slider handle directly.
        const displayValue = targetZoomScale === 1 && zoomScale > 0.98 && zoomScale < 1.05 ? 100 : Math.round(newValue);
        this.sliderHandleRef.current.innerHTML = `${displayValue}${zoomSliderOptions.unit}`;
    });

    // This is used to force re-renders when the options are changed externally
    forceRender = (): void => {
        this.setState({ forcedRenderTime: getTimestamp() });
    };
    debouncedForceRender = debounce(this.forceRender, 200);

    render(): ReactElement {
        const { zoomScale, isCurrentBoardFetched } = this.props;

        const scaleToFitButtonTargetScale = getTargetZoomToFitScale(zoomScale, zoomSliderOptions.scaleToFit);
        const showResetText = scaleToFitButtonTargetScale === 1;

        const scaleButtonClasses = classNames('secondary compact', {
            [SHOW_SCALE_CLASS]: !showResetText,
            [SHOW_RESET_CLASS]: showResetText,
        });

        return (
            <PopupPanelAsideSection className="ZoomPopupPanelSection">
                {!isCurrentBoardFetched && (
                    <div className="loading-overlay">
                        <Spinner show />
                    </div>
                )}
                <div className="title-container">
                    <h3>Zoom</h3>
                </div>
                <p className="explanation">Tip: hold {convertKeyToEnvironment('⌘')} and use mouse wheel to zoom</p>
                <Slider
                    handleRef={this.sliderHandleRef}
                    className="ZoomSlider"
                    {...zoomSliderOptions}
                    value={zoomScale * 100}
                    onChange={this.handleZoomChange}
                    onWheel={this.handleZoomWheel}
                    incrementValue={incrementZoomScalePercentage(zoomSliderOptions.min)}
                    decrementValue={decrementZoomScalePercentage(zoomSliderOptions.min)}
                    manual={true}
                />
                <StyledButton
                    ref={this.zoomToFitButtonRef}
                    className={scaleButtonClasses}
                    onClick={this.onScaleToFitClick}
                >
                    <div className="text-reset">Reset to 100%</div>
                    <div className="text-scale">Scale to fit</div>
                </StyledButton>
            </PopupPanelAsideSection>
        );
    }
}

export default ZoomPopupPanelSection;
